import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Ban, Edit3, Loader2, Trash2 } from 'lucide-react';
import Modal from '../../components/Modal';
import StatusBadge from '../../components/StatusBadge';
import { FormTextarea } from '../../components/FormComponents';
import { CONTRACT_STATUSES as STATUSES } from '../../constants/statuses';
import { MARKDOWN_COMPONENTS, formatDateTime } from './contractDetailUtils';

export default function ContractDetailModals({
  canWriteContracts,
  closeFileDeleteConfirm,
  contract,
  deleteContractDriveFolderWithLastFile,
  deleteContractWithLastFile,
  deleteDriveFile,
  deleteDriveFolder,
  deleting,
  deletingFileId,
  dsDraft,
  dsEditing,
  dsError,
  dsSaving,
  fileDeleteTarget,
  handleDelete,
  handleDeleteFile,
  handleDetailedSummarySave,
  handleStatusChange,
  handleTerminate,
  newExecutedDate,
  newStatus,
  setDeleteContractDriveFolderWithLastFile,
  setDeleteContractWithLastFile,
  setDeleteDriveFile,
  setDeleteDriveFolder,
  setDsDraft,
  setDsEditing,
  setNewExecutedDate,
  setNewStatus,
  setShowDeleteConfirm,
  setShowDetailedSummaryModal,
  setShowStatusModal,
  setShowTerminateConfirm,
  setTerminationReason,
  showDeleteConfirm,
  showDetailedSummaryModal,
  showStatusModal,
  showTerminateConfirm,
  terminationReason,
}) {
  const c = contract;

  return (
    <>
      {/* ================= Modals ================= */}

      {/* Status change */}
      <Modal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title="Change Status"
        footer={(
          <>
            <button onClick={() => setShowStatusModal(false)} className="btn outline sm">Cancel</button>
            <button onClick={handleStatusChange} className="btn primary sm">Update status</button>
          </>
        )}
      >
        <div className="card-body" style={{ overflowY: 'auto' }}>
          <div className="field-label" style={{ marginBottom: 8 }}>Select new status</div>
          {/* "Terminated" is intentionally absent — terminating a
              contract goes through the dedicated Terminate flow (More
              menu → Terminate), which captures a reason and writes it
              onto the status_history note. Allowing the generic Change
              Status modal to set 'terminated' would silently lose that
              reason field, so we filter it out here. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {STATUSES.filter((s) => s.value !== 'terminated').map(s => {
              const selected = newStatus === s.value;
              return (
                <label
                  key={s.value}
                  htmlFor={`status-radio-${s.value}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: 10,
                    borderRadius: 6,
                    border: selected ? '0.5px solid var(--border-focus)' : '0.5px solid var(--border-primary)',
                    background: selected ? 'var(--surface-hover)' : 'transparent',
                    cursor: 'pointer',
                    transition: 'background 120ms, border-color 120ms',
                  }}
                >
                  <input
                    type="radio"
                    id={`status-radio-${s.value}`}
                    name="status"
                    value={s.value}
                    checked={selected}
                    onChange={(e) => setNewStatus(e.target.value)}
                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                  />
                  <span style={{
                    width: 14, height: 14, borderRadius: '50%',
                    border: selected ? '1px solid var(--border-focus)' : '1px solid var(--border-secondary)',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {selected && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--text-primary)' }} />}
                  </span>
                  <StatusBadge status={s.value} size="small" />
                </label>
              );
            })}
          </div>

          {(newStatus === 'active' || newStatus === 'expired') && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '0.5px solid var(--border-primary)' }}>
              <label className="field-label">
                Executed Date
                {!contract?.executedDate && (
                  <span style={{ color: 'var(--text-tertiary)', fontWeight: 450, marginLeft: 4 }}>(optional)</span>
                )}
              </label>
              <div className="field-hint" style={{ marginBottom: 6 }}>
                When the contract was signed. Leave blank if you don't have the signed copy yet.
              </div>
              <input type="date" value={newExecutedDate} onChange={(e) => setNewExecutedDate(e.target.value)} className="input" />
            </div>
          )}
        </div>
      </Modal>

      {/* Detailed summary */}
      <Modal
        isOpen={showDetailedSummaryModal}
        onClose={() => { if (!dsSaving) { setDsEditing(false); setShowDetailedSummaryModal(false); } }}
        title={dsEditing ? 'Edit Detailed Summary' : 'Detailed Summary'}
        size="lg"
        footer={dsEditing ? (
          <>
            <button onClick={() => setDsEditing(false)} disabled={dsSaving} className="btn outline sm">Cancel</button>
            <button onClick={handleDetailedSummarySave} disabled={dsSaving} className="btn primary sm">
              {dsSaving ? 'Saving…' : 'Save'}
            </button>
          </>
        ) : (
          <>
            {canWriteContracts && (
              <button onClick={() => { setDsDraft(c.detailedSummary || ''); setDsEditing(true); }} className="btn outline sm">
                <Edit3 className="w-3.5 h-3.5" /> Edit
              </button>
            )}
            <button onClick={() => setShowDetailedSummaryModal(false)} className="btn outline sm">Close</button>
          </>
        )}
      >
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {dsEditing ? (
            <div className="card-body">
              <FormTextarea
                label="Detailed Summary (Markdown)"
                name="detailedSummary"
                value={dsDraft}
                onChange={(e) => setDsDraft(e.target.value)}
                rows={24}
                hint="Supports headings (## / ###), lists, tables. To regenerate, use Scan document in the More menu."
              />
              {dsError && (
                <div className="callout red" style={{ marginTop: 12, padding: '10px 12px' }}>
                  <div className="callout-body" style={{ fontSize: 12 }}>{dsError}</div>
                </div>
              )}
            </div>
          ) : (
            <div className="card-body">
              {c?.detailedSummary ? (
                <>
                  <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                    <ReactMarkdown components={MARKDOWN_COMPONENTS}>{c.detailedSummary}</ReactMarkdown>
                  </div>
                  <p style={{
                    marginTop: 20, paddingTop: 12, borderTop: '0.5px solid var(--border-primary)',
                    fontSize: 11, color: 'var(--text-tertiary)',
                  }}>
                    {c.detailedSummaryUpdatedAt
                      ? `Last edited ${formatDateTime(c.detailedSummaryUpdatedAt)}`
                      : `Auto-generated at ingest${c.extractionConfidence ? ` (${c.extractionConfidence} confidence)` : ''}.`}
                  </p>
                </>
              ) : (
                <p className="muted" style={{ fontSize: 13, margin: 0 }}>
                  No detailed summary yet. Upload a document via Scan, or click Edit to write one manually.
                </p>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Terminate */}
      <Modal
        isOpen={showTerminateConfirm}
        onClose={() => { setShowTerminateConfirm(false); setTerminationReason(''); }}
        title="Terminate Contract"
        footer={(
          <>
            <button onClick={() => { setShowTerminateConfirm(false); setTerminationReason(''); }} className="btn outline sm">Cancel</button>
            <button onClick={handleTerminate} className="btn primary sm">
              <Ban className="w-3.5 h-3.5" /> Terminate
            </button>
          </>
        )}
      >
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="callout yellow" style={{ alignItems: 'flex-start' }}>
            <div className="callout-icon"><Ban className="w-4 h-4" /></div>
            <div className="callout-body">
              <strong>Terminate this contract?</strong>
              <div className="muted" style={{ marginTop: 2, fontSize: 12 }}>
                This marks status as terminated. The contract record is preserved.
              </div>
            </div>
          </div>
          <div>
            <label className="field-label">
              Reason
              <span style={{ color: 'var(--text-tertiary)', fontWeight: 450, marginLeft: 4 }}>(optional)</span>
            </label>
            <textarea value={terminationReason} onChange={(e) => setTerminationReason(e.target.value)} rows={2} className="textarea" />
          </div>
        </div>
      </Modal>

      {/* Delete */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          // Ignore close attempts (backdrop click, Escape) while the
          // DELETE request is in flight — the Drive trash step can take
          // several seconds and we don't want the user accidentally
          // dismissing the "it's working" state mid-operation.
          if (deleting) return;
          setShowDeleteConfirm(false);
          setDeleteDriveFolder(true);
        }}
        title="Delete Contract Permanently"
        footer={(
          <>
            <button
              onClick={() => { setShowDeleteConfirm(false); setDeleteDriveFolder(true); }}
              className="btn outline sm"
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="btn danger sm"
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Deleting…
                </>
              ) : (
                <>
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete permanently
                </>
              )}
            </button>
          </>
        )}
      >
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="callout red" style={{ alignItems: 'flex-start' }}>
            <div className="callout-icon"><Trash2 className="w-4 h-4" /></div>
            <div className="callout-body">
              <strong>Permanently delete this contract?</strong>
              <div style={{ marginTop: 2, fontSize: 12 }}>
                This removes the contract row and all its history. <strong>Cannot be undone.</strong>
              </div>
            </div>
          </div>
          {c.driveContractFolderId && (
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: 10,
              borderRadius: 6, border: '0.5px solid var(--border-primary)',
              background: 'var(--surface-base)', cursor: 'pointer',
            }}>
              <input type="checkbox" checked={deleteDriveFolder}
                     onChange={(e) => setDeleteDriveFolder(e.target.checked)}
                     style={{ marginTop: 2, accentColor: 'var(--color-red-500)' }} />
              <div style={{ flex: 1, fontSize: 13 }}>
                <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>Also move the Drive folder to Trash</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                  Recoverable from Drive's Trash for 30 days.
                </div>
              </div>
            </label>
          )}
        </div>
      </Modal>

      {/* Delete file */}
      <Modal
        isOpen={!!fileDeleteTarget}
        onClose={closeFileDeleteConfirm}
        title="Delete File"
        footer={(
          <>
            <button
              onClick={closeFileDeleteConfirm}
              className="btn outline sm"
              disabled={!!deletingFileId}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteFile}
              className="btn danger sm"
              disabled={!!deletingFileId}
            >
              {deletingFileId ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Deleting…
                </>
              ) : fileDeleteTarget?.isOnlyFile && deleteContractWithLastFile ? (
                <>
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete contract
                </>
              ) : (
                <>
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete file
                </>
              )}
            </button>
          </>
        )}
      >
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="callout red" style={{ alignItems: 'flex-start' }}>
            <div className="callout-icon"><Trash2 className="w-4 h-4" /></div>
            <div className="callout-body">
              <strong>Remove this file from the contract?</strong>
              <div style={{ marginTop: 2, fontSize: 12 }}>
                {fileDeleteTarget?.isOnlyFile
                  ? 'This is the only file attached to this contract.'
                  : 'This removes only this file record. The contract and its other files stay in place.'}
              </div>
              {fileDeleteTarget?.name && (
                <div className="mono" style={{ marginTop: 8, fontSize: 11, wordBreak: 'break-word' }}>
                  {fileDeleteTarget.name}
                </div>
              )}
            </div>
          </div>
          {fileDeleteTarget?.driveFileId && !deleteContractWithLastFile && (
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: 10,
              borderRadius: 6, border: '0.5px solid var(--border-primary)',
              background: 'var(--surface-base)', cursor: deletingFileId ? 'default' : 'pointer',
            }}>
              <input
                type="checkbox"
                checked={deleteDriveFile}
                disabled={!!deletingFileId}
                onChange={(e) => setDeleteDriveFile(e.target.checked)}
                style={{ marginTop: 2, accentColor: 'var(--color-red-500)' }}
              />
              <div style={{ flex: 1, fontSize: 13 }}>
                <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>Also move this Drive file to Trash</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                  Only this file is moved. The contract folder and sibling files are not touched.
                </div>
              </div>
            </label>
          )}
          {fileDeleteTarget?.isOnlyFile && (
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: 10,
              borderRadius: 6, border: '0.5px solid var(--border-primary)',
              background: 'var(--surface-base)', cursor: deletingFileId ? 'default' : 'pointer',
            }}>
              <input
                type="checkbox"
                checked={deleteContractWithLastFile}
                disabled={!!deletingFileId}
                onChange={(e) => setDeleteContractWithLastFile(e.target.checked)}
                style={{ marginTop: 2, accentColor: 'var(--color-red-500)' }}
              />
              <div style={{ flex: 1, fontSize: 13 }}>
                <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>Also delete the contract record</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                  This deletes the contract row, history, and file records. This cannot be undone.
                </div>
              </div>
            </label>
          )}
          {fileDeleteTarget?.isOnlyFile && deleteContractWithLastFile && c.driveContractFolderId && (
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: 10,
              borderRadius: 6, border: '0.5px solid var(--border-primary)',
              background: 'var(--surface-base)', cursor: deletingFileId ? 'default' : 'pointer',
            }}>
              <input
                type="checkbox"
                checked={deleteContractDriveFolderWithLastFile}
                disabled={!!deletingFileId}
                onChange={(e) => setDeleteContractDriveFolderWithLastFile(e.target.checked)}
                style={{ marginTop: 2, accentColor: 'var(--color-red-500)' }}
              />
              <div style={{ flex: 1, fontSize: 13 }}>
                <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>Also move the contract Drive folder to Trash</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                  Uses the same Drive cleanup as Delete Contract and may also trash the empty counterparty folder.
                </div>
              </div>
            </label>
          )}
        </div>
      </Modal>
    </>
  );
}
